import React, { useState, useEffect } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import AppSidebar from '../../components/AppSidebar';
import AppHeader from '../../components/AppHeader';
import PageHeader from '../../components/PageHeader';
import TasksService from '../../services/TasksService';
import { FaRegEdit } from "react-icons/fa";
import { useForm } from 'react-hook-form';
import UsersService from '../../services/UsersService';
import { toast, ToastContainer } from 'react-toastify';
import Assign from '../../modals/Assign';
import ChangeStatusModal from '../../modals/ChangeStatus';
import ChangePriorityModal from './TaskModals/ChangePriority';
import RecentActivity from '../../components/RecentActivity';
import ViewCard from '../../components/ViewCard';

function ViewTask() {
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { formState: { errors }, control, handleSubmit } = useForm();
  const { control: statusControl, handleSubmit: handleStatusSubmit } = useForm();
  const { control: priorityControl, handleSubmit: handlePrioritySubmit } = useForm();
  const [AssigneeList, setAssigneeList] = useState([]);
  const status = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'awaiting_feedback', label: 'Awaiting Feedback' },
    { value: 'testing', label: 'Testing' },
  ];
  const priority = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const renderTags = (tags) => {
    try {
        const parsedTags = JSON.parse(tags);
        if (Array.isArray(parsedTags)) {
            return parsedTags.join(', ');
        }
    } catch (error) {
        return tags;
    }
    return tags;
};

  const fields = [
    { label: 'Subject', key: 'subject' },
    { label: 'Start Date', key: 'start_date' },
    { label: 'Due Date', key: 'due_date' },
    { label: 'Type', key: 'type' },
    { label: 'Tags', key: 'tags', render: renderTags },
    {
      label: 'Priority',
      key: 'priority',
      render: (priority) => {
      const badgeClass =
      priority === 'low' ? 'bg-primary' :
      priority === 'medium' ? 'bg-warning' :
      priority === 'high' ? 'bg-success' :
      'bg-danger';
      const statusText =
      priority === 'low' ? 'Low' :
      priority === 'medium' ? 'Medium' :
      priority === 'high' ? 'High' :
      'Urgent';
      
      return <span className={`badge ${badgeClass}`}>{statusText}</span>;
      }
      },
    {
      label: 'Status',
      key: 'status',
      render: (status) => {
      const badgeClass =
      status === 'not_started' ? 'bg-primary' :
      status === 'in_progress' ? 'bg-warning' :
      status === 'completed' ? 'bg-success' :
      status === 'on_hold' ? 'bg-danger' :
      status === 'testing' ? 'bg-secondary' :
      'bg-info';
      const statusText =
      status === 'not_started' ? 'Not Started' :
      status === 'in_progress' ? 'In Progress' :
      status === 'completed' ? 'Completed' :
      status === 'on_hold' ? 'On Hold' :
      status === 'testing' ? 'Testing' :
      'Awaiting Feedback';
      
      return <span className={`badge ${badgeClass}`}>{statusText}</span>;
      }
      },
    { label: 'Assignees', key: 'users', render: (users) => users && users.length > 0 
      ? users.map(user => user.name).join(', ') 
      : 'No Assignees' },
    { label: 'Description', key: 'description' },
];
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showPriorityModal, setShowPriorityModal] = useState(false);
    const handleCloseAssignModal = () => setShowAssignModal(false);
    const handleShowAssignModal = () => setShowAssignModal(true);
    const handleCloseStatusModal = () => setShowStatusModal(false);
    const handleShowStatusModal = () => setShowStatusModal(true);
    const handleClosePriorityModal = () => setShowPriorityModal(false);
    const handleShowPriorityModal = () => setShowPriorityModal(true);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        const response = await TasksService.getTask(id);
        if (response.status) {
          setTask(response.data.data);
        } else {
          setError(response.data.message);
        }
      } catch (error) {
        setError('Something went wrong');
      } finally {
        setLoading(false);
      }
    };

    const fetchAssignees = async () => {
      try {
        const response = await UsersService.list();
        const assignees = response.data.data;

        const formattedAssigneeList = assignees.map((assignee) => ({
          value: assignee.id,
          label: assignee.name + ' - ' + assignee.email,
        }));

        setAssigneeList(formattedAssigneeList);
      } catch (err) {
        toast.error('Failed to load assignees');
      }
    };

    fetchTask();
    fetchAssignees();
  }, [id]);

  const onSubmit = async (data) => {
    const assigneeIds = data.assignee.map((assignee) => assignee.value);
    const requestBody = {
      data: {
        assignee: assigneeIds
      }
    };

    try {
      const response = await TasksService.taskAssignToUser(id, requestBody);

      if (response.status === 200) {
        const taskResponse = await TasksService.getTask(id);
        if (taskResponse.status) {
          setTask(taskResponse.data.data);
        } else {
          toast.error('Failed to update task');
        }

        toast.success('Task assigned to user successfully');
        handleCloseAssignModal();
      } else {
        toast.error('Failed to assign task');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    }
  };

  const onSubmitStatus = async (data) => {
    try {
      const requestBody = {
        data: {
          status: data.status ? data.status.value : null
        }
      };

      const response = await TasksService.changeTaskStatus(id, requestBody);
      if (response.status === 200) {
        setTask(prevTask => ({ ...prevTask, status: data.status.value }));
        toast.success('Task status updated successfully');
        handleCloseStatusModal();
      } else {
        toast.error('Failed to update task status');
      }
    } catch (error) {
      toast.error('An error occurred while updating the task status');
    }
  };

  const onSubmitPriority = async (data) => {
    try {
      const requestBody = {
        data: {
          priority: data.priority ? data.priority.value : null
        }
      };

      const response = await TasksService.changeTaskPriority(id, requestBody);
      if (response.status === 200) {
        setTask(prevTask => ({ ...prevTask, priority: data.priority.value }));
        toast.success('Task priority updated successfully');
        handleClosePriorityModal();
      } else {
        toast.error('Failed to update task priority');
      }
    } catch (error) {
      toast.error('An error occurred while updating the task priority');
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <>
      <AppSidebar />
      <div className="main-content">
        <AppHeader />
        <div className="container-fluid">
          <div className="row">
            <PageHeader
              title="Task"
              button_label="Assign"
              button_label1="Back"
              link_to1="/tasks"
              buttonAttributes={{ onClick: handleShowAssignModal }}
              className="btn btn-sm btn-info me-2"
              className1="btn btn-primary btn-sm"
            />
            <div className="container card p-5">
              <div>
                <NavLink className="float-end pb-4" to={`/tasks/${id}/edit`}>
                  <FaRegEdit size={20} />
                </NavLink>
              </div>
              <div className="row">
              <ViewCard data={task} handleShowStatusModal = {handleShowStatusModal} handleShowPriorityModal={handleShowPriorityModal} fields={fields}/>
                <div className="col-12 col-md-12 col-lg-4 mb-4">
                <RecentActivity type ={"task"}/>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Assign Task Modal */}
        <Assign
          show={showAssignModal}
          handleClose={handleCloseAssignModal}
          handleSubmit={handleSubmit}
          control={control}
          AssigneeList={AssigneeList}
          onSubmit={onSubmit}
          errors={errors}
        />

        {/* Change Status Modal */}
        <ChangeStatusModal
          show={showStatusModal}
          handleClose={handleCloseStatusModal}
          handleSubmit={handleStatusSubmit}
          control={statusControl}
          status={status}
          onSubmit={onSubmitStatus}
          errors={errors}
        />

        {/* Change Status Modal */}
        <ChangePriorityModal
          show={showPriorityModal}
          handleClose={handleClosePriorityModal}
          handleSubmit={handlePrioritySubmit}
          control={priorityControl}
          priority={priority}
          onSubmit={onSubmitPriority}
          errors={errors}
        />

        <ToastContainer containerId={"toast-container"}/>
      </div>
    </>
  );
}

export default ViewTask;
